import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  notifications: boolean;
  language: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  fontSize: 'small' | 'medium' | 'large';
  animations: boolean;
}

interface AppState {
  userPreferences: UserPreferences;
  setUserPreferences: (preferences: Partial<UserPreferences>) => void;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  // Navigation
  navigationHistory: string[];
  addToHistory: (page: string) => void;
  goBack: () => void;
  // Authentication
  isAuthenticated: boolean;
  setIsAuthenticated: (authenticated: boolean) => void;
  // Feature flags
  featureFlags: Record<string, boolean>;
  setFeatureFlag: (flag: string, enabled: boolean) => void;
  // App settings
  isAppInitialized: boolean;
  setIsAppInitialized: (initialized: boolean) => void;
  // Error handling
  lastError: Error | null;
  setLastError: (error: Error | null) => void;
  // Performance
  performanceMetrics: {
    lastLoadTime: number;
    averageLoadTime: number;
    totalLoads: number;
  };
  updatePerformanceMetrics: (loadTime: number) => void;
}

const initialState: Omit<AppState, 
  'setUserPreferences' | 'toggleSidebar' | 'setCurrentPage' |
  'addToHistory' | 'goBack' |
  'setIsAuthenticated' |
  'setFeatureFlag' |
  'setIsAppInitialized' |
  'setLastError' |
  'updatePerformanceMetrics'
> = {
  userPreferences: {
    theme: 'dark',
    notifications: true,
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    fontSize: 'medium',
    animations: true,
  },
  isSidebarOpen: true,
  currentPage: '/',
  navigationHistory: [],
  isAuthenticated: false,
  featureFlags: {},
  isAppInitialized: false,
  lastError: null,
  performanceMetrics: {
    lastLoadTime: 0,
    averageLoadTime: 0,
    totalLoads: 0,
  },
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      ...initialState,
      setUserPreferences: (preferences) =>
        set((state) => ({
          userPreferences: { ...state.userPreferences, ...preferences },
        })),
      toggleSidebar: () =>
        set((state) => ({
          isSidebarOpen: !state.isSidebarOpen,
        })),
      setCurrentPage: (page) =>
        set((state) => ({
          currentPage: page,
          navigationHistory: [...state.navigationHistory, page],
        })),
      addToHistory: (page) =>
        set((state) => ({
          navigationHistory: [...state.navigationHistory, page],
        })),
      goBack: () =>
        set((state) => {
          const history = [...state.navigationHistory];
          history.pop(); // Remove current page
          const previousPage = history[history.length - 1] || '/';
          return {
            currentPage: previousPage,
            navigationHistory: history,
          };
        }),
      setIsAuthenticated: (authenticated) =>
        set(() => ({
          isAuthenticated: authenticated,
        })),
      setFeatureFlag: (flag, enabled) =>
        set((state) => ({
          featureFlags: {
            ...state.featureFlags,
            [flag]: enabled,
          },
        })),
      setIsAppInitialized: (initialized) =>
        set(() => ({
          isAppInitialized: initialized,
        })),
      setLastError: (error) =>
        set(() => ({
          lastError: error,
        })),
      updatePerformanceMetrics: (loadTime) =>
        set((state) => {
          const totalLoads = state.performanceMetrics.totalLoads + 1;
          const averageLoadTime =
            (state.performanceMetrics.averageLoadTime * state.performanceMetrics.totalLoads + loadTime) / totalLoads;
          return {
            performanceMetrics: {
              lastLoadTime: loadTime,
              averageLoadTime,
              totalLoads,
            },
          };
        }),
    }),
    {
      name: 'app-storage',
      partialize: (state) => ({
        userPreferences: state.userPreferences,
        isSidebarOpen: state.isSidebarOpen,
        featureFlags: state.featureFlags,
        isAppInitialized: state.isAppInitialized,
        performanceMetrics: state.performanceMetrics,
      }),
    }
  )
); 